import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Gauge, Settings, Zap, ArrowRight, ArrowLeft, X, Check, ChevronRight } from 'lucide-react';
import { PRODUCTS, CATEGORIES } from '../data/products';
import { COMPANY } from '../data/content';
import ProductImage from '../components/ProductImage';
import SEO from '../components/SEO';
import useFocusTrap from '../hooks/useFocusTrap';

const catIcon   = { Measurement: Gauge, Setting: Settings, Enhancement: Zap };
const catColour = { Measurement: '#F97316', Setting: '#3B82F6', Enhancement: '#10B981' };
const catBg     = { Measurement: '#FFF7ED', Setting: '#EFF6FF', Enhancement: '#F0FDF4' };

/* ─── Large-number hero spec block ─────────────────────────────── */
function HeroSpec({ spec }) {
  if (!spec) return null;
  // Support both string (legacy) and object { num, unit, label }
  if (typeof spec === 'string') {
    return (
      <div className="px-4 py-3.5 mb-6" style={{ background: '#FFF7ED', borderLeft: '3px solid #F97316' }}>
        <p className="font-mono text-[9px] uppercase tracking-wider text-safety mb-0.5">Key Specification</p>
        <p className="font-heading text-base font-bold text-steel-900 uppercase tracking-wide leading-tight">{spec}</p>
      </div>
    );
  }
  return (
    <div className="mb-6 p-5" style={{ background: '#FFF7ED', borderLeft: '4px solid #F97316' }}>
      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-safety mb-2">Key Specification</p>
      <div className="flex items-baseline gap-2 mb-1">
        {/* Large number */}
        <span
          className="font-heading font-bold leading-none"
          style={{ fontSize: '52px', color: '#F97316', letterSpacing: '-0.02em' }}
        >
          {spec.num}
        </span>
        {/* Unit */}
        {spec.unit && (
          <span
            className="font-heading font-semibold uppercase"
            style={{ fontSize: '22px', color: '#FB923C', letterSpacing: '0.04em' }}
          >
            {spec.unit}
          </span>
        )}
      </div>
      {/* Label */}
      <p className="font-body text-steel-600 text-sm leading-snug">{spec.label}</p>
    </div>
  );
}

/* ─── Product Modal ─────────────────────────────────────────────── */
function ProductModal({ product, onClose }) {
  const navigate = useNavigate();
  const idx  = PRODUCTS.findIndex(p => p.id === product.id);
  const prev = idx > 0 ? PRODUCTS[idx - 1] : null;
  const next = idx < PRODUCTS.length - 1 ? PRODUCTS[idx + 1] : null;
  const Icon = catIcon[product.cat] || Gauge;
  const isNew = product.sub && product.sub.includes('NEW');

  const trapRef = useFocusTrap(onClose);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const goTo = (id) => navigate(`/products/${id}`);

  return (
    <>
      <SEO
        title={product.name}
        description={product.desc ? product.desc.slice(0, 155) + '…' : ''}
        product={product}
      />

      {/* Backdrop — full viewport, behind modal */}
      <div
        className="fixed inset-0 z-[190] bg-stone-900/60 backdrop-blur-sm backdrop-enter"
        onClick={onClose}
      />

      {/* Modal container — starts below navbar (top: 64px) */}
      <div
        className="fixed z-[200] left-0 right-0 bottom-0 flex items-start justify-center"
        style={{ top: '64px', padding: '8px 12px 12px' }}
        role="dialog"
        aria-modal="true"
        aria-label={product.name}
        onClick={onClose}
      >
        <div
          ref={trapRef}
          className="modal-enter relative bg-white w-full flex flex-col font-body"
          style={{ maxWidth: '1080px', height: '100%' }}
          onClick={e => e.stopPropagation()}
        >
          {/* ── Navigation bar ── */}
          <div className="flex items-center justify-between px-5 md:px-7 py-3 border-b border-steel-200 flex-shrink-0 bg-white">
            <button
              onClick={onClose}
              className="font-mono text-[11px] uppercase tracking-wider text-steel-400 hover:text-steel-900 transition-colors flex items-center gap-1.5"
            >
              <ArrowLeft size={12} /> All Products
            </button>

            <div className="flex items-center gap-3 md:gap-5">
              <button
                onClick={() => prev && goTo(prev.id)}
                disabled={!prev}
                className={`font-mono text-[11px] uppercase tracking-wider flex items-center gap-1.5 transition-colors ${
                  prev ? 'text-steel-500 hover:text-safety cursor-pointer' : 'text-steel-200 cursor-default'
                }`}
              >
                <ArrowLeft size={11} />
                <span className="hidden sm:inline" style={{ maxWidth: '110px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', display: 'inline-block' }}>
                  {prev ? prev.shortName : '—'}
                </span>
              </button>

              <span className="font-mono text-[11px] text-steel-300 select-none tabular-nums">
                {idx + 1} / {PRODUCTS.length}
              </span>

              <button
                onClick={() => next && goTo(next.id)}
                disabled={!next}
                className={`font-mono text-[11px] uppercase tracking-wider flex items-center gap-1.5 transition-colors ${
                  next ? 'text-steel-500 hover:text-safety cursor-pointer' : 'text-steel-200 cursor-default'
                }`}
              >
                <span className="hidden sm:inline" style={{ maxWidth: '110px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', display: 'inline-block' }}>
                  {next ? next.shortName : '—'}
                </span>
                <ArrowRight size={11} />
              </button>

              <button
                onClick={onClose}
                className="p-1.5 hover:bg-steel-100 transition-colors ml-1"
                aria-label="Close"
              >
                <X size={16} className="text-steel-400" />
              </button>
            </div>
          </div>

          {/* ── Body ── */}
          <div className="flex flex-1 overflow-hidden flex-col md:flex-row min-h-0">

            {/* Left — image panel */}
            <div
              className="md:w-[42%] flex-shrink-0 border-b md:border-b-0 md:border-r border-steel-200 flex flex-col items-center justify-center relative"
              style={{ background: catBg[product.cat] || '#F8FAFC', minHeight: '180px' }}
            >
              <ProductImage
                src={product.image}
                alt={product.name}
                className="w-full h-full object-contain"
                style={{ padding: '32px', maxHeight: '420px' }}
                eager={idx === 0}
              />
              {/* Category badge */}
              <div className="absolute top-4 left-4">
                <span
                  className="font-mono text-[10px] uppercase tracking-wider px-2.5 py-1.5 bg-white border border-steel-200 flex items-center gap-1.5 shadow-sm"
                  style={{ color: catColour[product.cat] || '#F97316' }}
                >
                  <Icon size={10} strokeWidth={2.5} />
                  {product.cat}
                </span>
              </div>
              {/* NEW badge */}
              {isNew && (
                <div className="absolute top-4 right-4">
                  <span className="font-mono text-[10px] uppercase tracking-wider bg-safety text-white px-2.5 py-1.5">
                    New 2026
                  </span>
                </div>
              )}
            </div>

            {/* Right — scrollable info */}
            <div className="flex-1 flex flex-col overflow-hidden min-h-0">
              <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6">

                {/* ID + category breadcrumb */}
                <p className="font-mono text-[10px] uppercase tracking-wider text-steel-300 mb-1">
                  #{String(product.id).padStart(2, '0')} &middot; {product.cat}
                </p>

                {/* Product name */}
                <h2 className="font-heading text-2xl md:text-[28px] font-bold uppercase tracking-tight text-steel-900 leading-[1.05] mb-2">
                  {product.name}
                </h2>

                {/* Sub-title */}
                <p className="font-mono text-[11px] uppercase tracking-wider mb-5" style={{ color: '#F97316' }}>
                  {product.sub}
                </p>

                {/* ── Large-number heroSpec ── */}
                <HeroSpec spec={product.heroSpec} />

                {/* Description */}
                <p className="font-body text-steel-500 text-[15px] leading-relaxed mb-7">
                  {product.desc}
                </p>

                {/* Specs + Compat — 2 columns */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 mb-7">
                  <div>
                    <h3 className="font-heading text-sm uppercase tracking-wider text-steel-900 mb-3 flex items-center gap-2">
                      <div className="w-4 h-0.5 flex-shrink-0" style={{ background: '#F97316' }} />
                      Specifications
                    </h3>
                    {product.specs.map((spec, i) => (
                      <div key={i} className="flex items-start gap-2.5 py-2 border-b border-steel-100 last:border-0">
                        <Check size={11} className="text-safety mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                        <span className="font-mono text-[11px] text-steel-600 leading-snug">{spec}</span>
                      </div>
                    ))}
                  </div>

                  <div>
                    <h3 className="font-heading text-sm uppercase tracking-wider text-steel-900 mb-3 flex items-center gap-2">
                      <div className="w-4 h-0.5 flex-shrink-0" style={{ background: '#F97316' }} />
                      Compatibility
                    </h3>
                    {product.compat.map((c, i) => (
                      <div key={i} className="flex items-start gap-2.5 py-2 border-b border-steel-100 last:border-0">
                        <ChevronRight size={11} className="text-steel-300 mt-0.5 flex-shrink-0" />
                        <span className="font-mono text-[11px] text-steel-600 leading-snug">{c}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Proven Results */}
                <h3 className="font-heading text-sm uppercase tracking-wider text-steel-900 mb-3 flex items-center gap-2">
                  <div className="w-4 h-0.5 flex-shrink-0" style={{ background: '#F97316' }} />
                  Proven Results
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {product.results.map((r, i) => (
                    <div
                      key={i}
                      className="px-4 py-3"
                      style={{ background: '#FAFAF8', border: '1px solid #E2E8F0', borderLeft: '2px solid #FED7AA' }}
                    >
                      <span className="font-mono text-[11px] text-steel-700 leading-relaxed">{r}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fixed CTA bar */}
              <div className="border-t border-steel-200 px-6 md:px-8 py-4 bg-white flex-shrink-0">
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={`https://wa.me/919843035014?text=Hi%20Apex%20Precitech%2C%20I%20would%20like%20to%20know%20more%20about%20${encodeURIComponent(product.name)}.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-safety text-white font-heading uppercase text-sm tracking-wider py-3.5 text-center hover:-translate-y-0.5 hover:bg-orange-600 transition-all duration-200"
                  >
                    Get Quote on WhatsApp
                  </a>
                  <a
                    href={`tel:${COMPANY.phoneRaw}`}
                    className="flex-1 border-2 border-steel-200 text-steel-700 font-heading uppercase text-sm tracking-wider py-3.5 text-center hover:border-steel-900 hover:text-steel-900 transition-all duration-200"
                  >
                    Call Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Products Page ─────────────────────────────────────────────── */
export default function ProductsPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [active, setActive] = useState('All');

  const selectedProduct = id ? PRODUCTS.find(p => p.id === Number(id)) : null;
  const filtered = active === 'All' ? PRODUCTS : PRODUCTS.filter(p => p.cat === active);

  return (
    <>
      <SEO
        title="Instruments"
        description="9 precision measurement and setting instruments for textile spinning — Top Arm Load Gauges, Spindle Concentricity, Saddle Setting and more."
      />

      <div data-testid="products-page" className="pt-16 min-h-screen bg-white">

        {/* Page header */}
        <section className="py-12 md:py-14 border-b border-steel-200" style={{ background: '#FAFAF8' }}>
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-[2px] bg-safety" />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-safety">Product Catalog</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-steel-900 uppercase tracking-tight mb-3">
              Our Instruments
            </h1>
            <p className="font-body text-steel-500 text-base max-w-lg">
              Precision measurement and setting devices for every stage of the textile spinning process.
            </p>
          </div>
        </section>

        {/* Filter bar — sticky */}
        <div className="border-b border-steel-200 bg-white sticky top-16 z-40">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex gap-0.5 overflow-x-auto py-2.5" data-testid="product-filter">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActive(cat)}
                  className={`font-mono text-[11px] uppercase tracking-wider px-4 py-2 whitespace-nowrap transition-all duration-200 ${
                    active === cat
                      ? 'bg-steel-900 text-white'
                      : 'text-steel-500 hover:text-steel-900 hover:bg-steel-50'
                  }`}
                >
                  {cat}
                  <span className="ml-1 opacity-50">
                    ({cat === 'All' ? PRODUCTS.length : PRODUCTS.filter(p => p.cat === cat).length})
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Product grid — 2 cols mobile, 3 cols desktop */}
        <section className="py-10 md:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 border border-steel-200">
              {filtered.map((p, i) => {
                const Icon  = catIcon[p.cat] || Gauge;
                const isNew = p.sub && p.sub.includes('NEW');
                // Hero spec label for the card
                const specLabel = p.heroSpec
                  ? (typeof p.heroSpec === 'string' ? p.heroSpec : `${p.heroSpec.num}${p.heroSpec.unit ? ' ' + p.heroSpec.unit : ''} — ${p.heroSpec.label}`)
                  : null;
                return (
                  <div
                    key={p.id}
                    data-testid={`product-item-${p.id}`}
                    onClick={() => navigate(`/products/${p.id}`)}
                    className="group cursor-pointer border-b border-r border-steel-200 hover:bg-orange-50/50 transition-colors duration-200 flex flex-col relative"
                  >
                    {isNew && (
                      <span className="absolute top-2.5 right-2.5 z-10 font-mono text-[9px] uppercase tracking-wider bg-safety text-white px-2 py-0.5">
                        New
                      </span>
                    )}

                    {/* Image — full colour, object-contain, category-tinted background */}
                    <div
                      className="flex-shrink-0 overflow-hidden"
                      style={{
                        background: catBg[p.cat] || '#F8FAFC',
                        borderBottom: '1px solid #E2E8F0',
                        height: '180px',
                      }}
                    >
                      <ProductImage
                        src={p.image}
                        alt={p.name}
                        className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                        style={{ padding: '16px' }}
                        eager={i < 2}
                      />
                    </div>

                    <div className="p-4 sm:p-5 flex flex-col flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1">
                          <Icon size={10} style={{ color: catColour[p.cat] }} strokeWidth={2.5} />
                          <span className="font-mono text-[9px] uppercase tracking-wider text-steel-400">{p.cat}</span>
                        </div>
                        <span className="font-mono text-[9px] text-steel-300 hidden sm:block">#{String(p.id).padStart(2, '0')}</span>
                      </div>

                      <h3 className="font-heading text-sm md:text-base uppercase tracking-wide text-steel-900 mb-1 group-hover:text-safety transition-colors leading-tight">
                        {p.name}
                      </h3>
                      <p className="font-mono text-[9px] sm:text-[10px] text-steel-400 uppercase tracking-wider mb-3 leading-snug">
                        {p.sub}
                      </p>

                      {/* Key spec chip */}
                      {specLabel && (
                        <div
                          className="px-2.5 py-1.5 mb-3 hidden sm:block"
                          style={{ background: '#FFF7ED', borderLeft: '2px solid #F97316' }}
                        >
                          <span className="font-mono text-[10px] text-safety leading-tight line-clamp-2">
                            {typeof p.heroSpec === 'object'
                              ? <><strong style={{ fontSize: '13px' }}>{p.heroSpec.num}{p.heroSpec.unit ? ' ' + p.heroSpec.unit : ''}</strong> — {p.heroSpec.label}</>
                              : specLabel
                            }
                          </span>
                        </div>
                      )}

                      <span className="font-mono text-[10px] uppercase tracking-wider text-safety opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 mt-auto">
                        View Details <ArrowRight size={9} />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>

      {/* Modal */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => navigate('/products')}
        />
      )}
    </>
  );
}
